<template>
  <div class="h-[375px]">
    <VeeForm v-slot="{ handleSubmit, meta }" as="">
      <form
        autocomplete="off"
        class="w-[22rem]"
        @submit="handleSubmit($event, onSubmit)"
      >
        <div
          class="bg-neutral-secondary rounded-md p-4 max-h-[320px] h-full overflow-scroll"
        >
          <VeeField v-slot="{ field }" name="portfolio">
            <NCheckbox
              v-model="formModel.portfolio"
              v-bind="field"
              :label="t('modules.news.myPortfolio')"
            />
          </VeeField>

          <VeeField
            v-slot="{ field }"
            v-model="formModel.instruments"
            name="instruments"
          >
            <div class="mt-3">
              <NText
                class="whitespace-nowrap text-neutral-secondary-press block mb-1 pr-3"
                variant="caption1"
                weight="medium"
              >
                {{ t('news.symbolNameOrCompany') }}
              </NText>

              <NListbox
                v-model="formModel.instruments"
                v-bind="field"
                input-size="small"
                :placeholder="t('common.selectSymbolOrCompany')"
                multiple
                :input-props="listboxSearchInputProps"
                :disabled="Boolean(formModel.portfolio)"
                @update:query="setInstrumentsQuery"
              >
                <NListboxOption
                  v-for="(symbol, index) of symbols"
                  :key="symbol.isin ?? `sym-${index}`"
                  :value="symbol.isin"
                  :label="symbol.name"
                >
                  <Retail v-if="symbol.isin" :isin="symbol.isin">
                    <NText weight="medium" variant="caption1">
                      {{ symbol.name }}
                    </NText>
                  </Retail>
                </NListboxOption>
              </NListbox>
            </div>
          </VeeField>

          <VeeField v-slot="{ field }" name="fromDate">
            <div class="mt-3">
              <NText
                class="whitespace-nowrap text-neutral-secondary-press pr-3"
                variant="caption1"
                weight="bold"
              >
                {{ t('common.fromDate') }}
              </NText>

              <Datepicker
                v-bind="field"
                v-model="formModel.fromDate"
                class="mt-1"
              />
            </div>
          </VeeField>

          <VeeField v-slot="{ field }" name="toDate">
            <div class="mt-3">
              <NText
                class="whitespace-nowrap text-neutral-secondary-press pr-3"
                variant="caption1"
                weight="bold"
              >
                {{ t('common.toDate') }}
              </NText>

              <Datepicker
                v-bind="field"
                v-model="formModel.endDate"
                class="mt-1"
              />
            </div>
          </VeeField>
        </div>

        <div class="mt-4 w-full">
          <NButton
            :disabled="!meta.dirty"
            variant="fill"
            type="submit"
            color="green"
            class="w-full mt-2"
          >
            {{ t('common.applyFilters') }}
          </NButton>
        </div>
      </form>
    </VeeForm>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useDialog } from '@negar/core';
import { watchDebounced } from '@vueuse/core';
import { useSearchSymbolQuery } from '@core/symbol';
import { queryStringToJson, toShortDate, translate } from '@libs/utils';

import { Retail } from '@/modules/symbol';

import Datepicker from '@/components/Datepicker/index.vue';
import { toGregorian } from '@/components/Datepicker/convertor';
import { DEFAULT_DATE } from '@/components/Datepicker/constants';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { remove: removeOrdersFilterDialog } = useDialog();

const { portfolio = false, fromDate, endDate, instruments = [] } = route.query;

const formModel = reactive({
  portfolio: portfolio ?? false,
  fromDate: fromDate
    ? new Date(fromDate as string)
    : new Date(...toGregorian({ ...DEFAULT_DATE })),
  endDate: endDate ? new Date(endDate as string) : new Date(),
  instruments: queryStringToJson(instruments as string[] | undefined),
});

const instrumentsQuery = ref('');

const listboxSearchInputProps = {
  readonly: false,
};

const setInstrumentsQuery = (value: string) => {
  instrumentsQuery.value = translate(String(value ?? ''));
};
const { symbols, searchSymbols } = useSearchSymbolQuery(instrumentsQuery);

watchDebounced(instrumentsQuery, searchSymbols, { debounce: 50 });

const onSubmit = () => {
  const convertedFormModel = {
    ...route.query,
    fromDate: toShortDate(formModel.fromDate),
    endDate: toShortDate(formModel.endDate),
  };

  if (formModel.instruments && formModel.instruments.length) {
    convertedFormModel['instruments'] = JSON.stringify(formModel.instruments);
  }

  if (formModel.portfolio) {
    convertedFormModel['portfolio'] = true;
  } else {
    delete convertedFormModel['portfolio'];
  }

  router.push({
    path: route.path,
    query: convertedFormModel,
  });

  removeOrdersFilterDialog();
};
</script>
